/**
 * BÁO LỖI CHUNG CỦA MỘT BIỂU MẪU — OQ-121, mở rộng ở OQ-161.
 *
 * ── LỖI SINH RA TỆP NÀY (OQ-121) ──────────────────────────────────────────
 * Chủ dự án thêm `google.com`, bấm *Thêm tên miền*, và **không có gì xảy ra**.
 * Máy chủ trả về đầy đủ:
 *
 *     502 · CLOUDFLARE_ERROR · mã 1097
 *     "This web property cannot be added to Cloudflare at this time…"
 *
 * Modal bắt lỗi vào state rồi chỉ đọc ra qua `error.fieldMessage('name')`.
 * `CLOUDFLARE_ERROR` **không có trường**, nên `fieldMessage()` trả `undefined`
 * và không gì được vẽ. Nút hết trạng thái "đang xử lý", modal đứng yên.
 *
 * ── LỖ THỨ BA, BỊT Ở OQ-161 ───────────────────────────────────────────────
 * Bịt hai lối vào chưa đủ. Có ba:
 *
 *   1. lỗi KHÔNG có trường                → báo chung ở đầu biểu mẫu (OQ-121) ✔
 *   2. lỗi có trường, trường CÓ MẶT       → ô nhập tự hiện lỗi                ✔
 *   3. lỗi có trường, trường KHÔNG được vẽ → **không gì hiện ra**             ✘
 *
 * Ca (3) gặp thật: hộp thoại *Thêm tên miền* chỉ vẽ ô *Tài khoản Cloudflare*
 * khi token phủ từ **hai** tài khoản trở lên (OQ-120). Với một tài khoản —
 * đúng cấu hình của chủ dự án — `cfAccountId` vẫn được gửi, máy chủ chặn bằng
 * `^[0-9a-f]{32}$`, trả 422 kèm `fields: [{ field: 'cfAccountId' }]`, và màn
 * hình **im lặng hoàn toàn**. Lại đúng dạng nguy nhất: người dùng không kết
 * luận *"hỏng rồi"* mà kết luận *"mình bấm hụt"*, rồi bấm lại.
 *
 * ── VÌ SAO KHÔNG NHẬN MỘT DANH SÁCH TRƯỜNG ĐANG VẼ ────────────────────────
 * Cách hiển nhiên là cho component một prop `fields={['name', 'plan', …]}`.
 * Không làm, vì đó là **một bảng phải bảo trì bằng tay ở mỗi nơi gọi**: thêm
 * một ô mà quên thêm vào danh sách thì lỗi của ô ấy bị báo **hai lần**; xoá
 * một ô mà quên xoá khỏi danh sách thì lỗ hổng (3) quay lại y nguyên. Bảng như
 * vậy sai âm thầm, và sai đúng vào lúc ai đó sửa biểu mẫu — tức lúc không ai
 * đang nghĩ về tệp này.
 *
 * ── NÊN ĐO THẲNG THỨ CẦN BẢO ĐẢM ──────────────────────────────────────────
 * Bất biến là *"mọi thông điệp lỗi phải hiện ra chữ nào đó"*. Nên phép kiểm
 * hỏi đúng câu ấy: **thông điệp này có nằm trong DOM không?** Không danh sách,
 * không đăng ký, không đổi một nơi gọi nào — ô nào được vẽ thì chữ của nó có
 * mặt, ô nào không thì không.
 *
 * Đọc DOM phải sau khi vẽ nên qua `useEffect`: lượt vẽ đầu chưa có báo chung,
 * lượt sau mới có. Với một lỗi xuất hiện sau khi bấm gửi thì một khung hình là
 * không nhìn thấy được.
 */

import { useEffect, useRef, useState } from 'react';
import type { ApiError } from '../lib/api';
import { Icon } from './Icon';

/**
 * Gom chữ trong `goc`, **trừ** phần nằm trong `tru`.
 *
 * Phải trừ chính mình, nếu không lượt chạy effect thứ hai sẽ thấy thông điệp
 * mà ta vừa vẽ ra rồi kết luận "ô nhập đã hiện rồi" — báo chung tự xoá mình.
 * React ở chế độ nghiêm ngặt gọi effect hai lần, nên đây không phải lo xa.
 */
function chuNgoai(goc: Node, tru: Node | null): string {
  const walker = document.createTreeWalker(goc, NodeFilter.SHOW_TEXT);
  let ra = '';
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (tru && tru.contains(node)) continue;
    ra += node.nodeValue ?? '';
  }
  return ra;
}

/** Vùng hợp lý để tìm: biểu mẫu chứa nó, rồi tới hộp thoại, cuối cùng cả trang. */
function vungTim(neo: Element | null): Node {
  return neo?.closest('form') ?? neo?.closest('.modal') ?? document.body;
}

/**
 * Vẽ thông điệp lỗi mà **không ô nhập nào đang hiện**.
 *
 * Im lặng khi mọi trường lỗi đều đã có ô nhập hiện chúng — lặp lại ở đầu biểu
 * mẫu vừa thừa vừa khiến người dùng tưởng có hai lỗi.
 */
export function ApiErrorAlert({ error }: { error: ApiError | null | undefined }) {
  const neoRef = useRef<HTMLDivElement>(null);
  const [boSot, setBoSot] = useState<string[]>([]);

  useEffect(() => {
    const coChu = (error?.fields ?? [])
      .map((f) => f.message)
      .filter((m): m is string => typeof m === 'string' && m.trim().length > 0);

    if (coChu.length === 0) {
      setBoSot([]);
      return;
    }
    const dangHien = chuNgoai(vungTim(neoRef.current), neoRef.current);
    setBoSot(coChu.filter((message) => !dangHien.includes(message)));
  }, [error]);

  /*
   * Rơi về thông điệp chung khi KHÔNG có chữ nào của trường để hiện — OQ-161.
   *
   * Hai ca dẫn tới đây, và bản đầu của tệp này bỏ sót ca thứ hai (chính test
   * dưới bắt được):
   *
   *   · `fields` rỗng — đường cũ của OQ-121.
   *   · `fields` CÓ mục, nhưng không mục nào kèm `message`. Máy chủ được phép
   *     trả vậy. Khi ấy không ô nhập nào có gì để hiện, nên im lặng ở đây là im
   *     lặng thật — không phải "ô nhập lo rồi".
   *
   * Cả hai biết ngay lúc vẽ, không phải đợi effect.
   */
  const khongCoChuTruong =
    !error || error.fields.every((f) => !f.message || f.message.trim().length === 0);
  const chung = error && khongCoChuTruong ? error.message : null;
  const hien = chung ? [chung] : error ? boSot : [];

  return (
    <div ref={neoRef}>
      {hien.length > 0 ? (
        <div className="form-alert bad" role="alert">
          <Icon name="warn" size={16} />
          <span>{hien.join(' · ')}</span>
        </div>
      ) : null}
    </div>
  );
}
